import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDealValue } from "@/lib/formatDealValue";
import { exportOffersPdf } from "./exportOffersPdf";
import ftcLogo from "@assets/ftc-logo-light-bg.png";
import {
  deriveEv, rolloverDollars, cashAtClosing, totalConsideration, formatMultipleRange,
  type EvPoints,
} from "@shared/offerMath";
import { useOffers, type OfferRow, type OfferStage } from "@/hooks/useBuyerTracker";
import type { OfferQueueItem } from "./OfferTermsDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
  onEdit: (stage: OfferStage, item: OfferQueueItem) => void;
}

type SortKey = "multiple" | "ev" | "cash" | "received";

const money = (n: number | null) => (n != null ? formatDealValue(String(Math.round(n))) : "—");
const dt = (v: string | null) => (v ? new Date(v).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" }) : "—");

interface Computed {
  offer: OfferRow;
  ev: EvPoints;
  cash: { low: number; mid: number; high: number };
  midMultiple: number;
  totalMid: number | null;
}

/** Low/Mid/High cells for one value group; single-number bids span the group. */
function PointCells({ v, cls = "text-slate-800 dark:text-slate-300" }: { v: { low: number; mid: number; high: number }; cls?: string }) {
  if (v.low === v.high) {
    return (
      <td colSpan={3} className={`border-l border-slate-100 p-2.5 text-center text-[13px] font-bold tabular-nums ${cls}`}>
        {money(v.mid)}
      </td>
    );
  }
  return (
    <>
      {[v.low, v.mid, v.high].map((p, i) => (
        <td key={i} className={`p-2.5 text-right font-semibold tabular-nums ${cls} ${i === 0 ? "border-l border-slate-100 dark:border-slate-900" : ""} ${i === 1 ? "font-bold" : ""}`}>
          {money(p)}
        </td>
      ))}
    </>
  );
}

export function ReviewOffersModal({ open, onClose, clientId, onEdit }: Props) {
  const { data, isLoading } = useOffers(clientId, open);
  const [tab, setTab] = useState<OfferStage>("ioi");
  const [tabTouched, setTabTouched] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "multiple", dir: "desc" });

  // Default to LOI when any exist — that's the live stage of the process.
  useEffect(() => {
    if (open && data && !tabTouched) setTab(data.loi.length > 0 ? "loi" : "ioi");
  }, [open, data, tabTouched]);
  useEffect(() => { if (!open) setTabTouched(false); }, [open]);

  const ebitda = data?.ebitda ?? null;

  // Shares ClientDetail's cache entry, so this is a cache hit in practice
  const { data: client } = useQuery<{ name: string }>({
    queryKey: ["company", clientId],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${clientId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!clientId && open,
  });

  const computed: Computed[] = useMemo(() => {
    const offers = (data ? data[tab] : []) as OfferRow[];
    return offers.map((o) => {
      const t = o.terms;
      const ev = deriveEv(t, ebitda);
      return {
        offer: o, ev,
        cash: { low: cashAtClosing(t, ev.low), mid: cashAtClosing(t, ev.mid), high: cashAtClosing(t, ev.high) },
        midMultiple: (t.multipleLow + t.multipleHigh) / 2,
        totalMid: totalConsideration(t, ev.mid),
      };
    });
  }, [data, tab, ebitda]);

  const sorted = useMemo(() => {
    const val = (c: Computed): number => {
      switch (sort.key) {
        case "multiple": return c.midMultiple;
        case "ev": return c.ev.mid;
        case "cash": return c.cash.mid;
        case "received": return c.offer.receivedAt ? new Date(c.offer.receivedAt).getTime() : 0;
      }
    };
    return [...computed].sort((a, b) => {
      const aPassed = a.offer.status === "passed" ? 1 : 0, bPassed = b.offer.status === "passed" ? 1 : 0;
      if (aPassed !== bPassed) return aPassed - bPassed; // passed always sink to the bottom
      return sort.dir === "desc" ? val(b) - val(a) : val(a) - val(b);
    });
  }, [computed, sort]);

  const clickSort = (key: SortKey) =>
    setSort((p) => (p.key === key ? { key, dir: p.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === "desc" ? " ▼" : " ▲") : "");

  const toQueueItem = (o: OfferRow): OfferQueueItem => ({
    linkId: o.linkId, buyerName: o.buyerName,
    existing: o.terms,
    ioiRef: data?.ioi.find((i) => i.linkId === o.linkId)?.terms ?? null,
    alreadyMarked: true,
  });

  const handleExportPdf = () => {
    exportOffersPdf({
      clientName: client?.name ?? "Client",
      stage: tab,
      ebitda,
      // Blob documents don't resolve app-relative paths — make it absolute
      logoUrl: new URL(ftcLogo, window.location.origin).href,
      rows: sorted.map((c, i) => {
        const t = c.offer.terms;
        return {
          rank: i + 1,
          buyerName: c.offer.buyerName,
          firmType: c.offer.buyerFirmType,
          passed: c.offer.status === "passed",
          multiple: formatMultipleRange(t.multipleLow, t.multipleHigh),
          ev: c.ev,
          cash: c.cash,
          rolloverPct: t.rolloverPct,
          rolloverMid: rolloverDollars(t.rolloverPct, c.ev.mid),
          earnoutAmount: t.earnoutAmount,
          earnoutInEv: t.earnoutInEv,
          totalMid: c.totalMid,
          sellerFinancing: t.sellerFinancingAmount,
          received: c.offer.receivedAt,
          notes: t.notes,
        };
      }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto xl:max-w-7xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-x-4 gap-y-2">
            Review Offers
            <span className="flex gap-1 rounded-lg bg-slate-100 p-0.5 text-sm font-medium dark:bg-slate-900/30">
              {(["ioi", "loi"] as const).map((s) => (
                <button
                  key={s}
                  className={`rounded-md px-3 py-1 ${tab === s ? "bg-white font-bold text-slate-900 shadow-sm dark:text-slate-200" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"}`}
                  onClick={() => { setTab(s); setTabTouched(true); }}
                >
                  {s.toUpperCase()} · {data ? data[s].length : 0}
                </button>
              ))}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto mr-6"
              disabled={sorted.length === 0}
              onClick={handleExportPdf}
            >
              Export PDF
            </Button>
          </DialogTitle>
        </DialogHeader>

        {ebitda == null && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400">
            No EBITDA set on the client overview — showing EV as entered. Set EBITDA to see multiples move with it.
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-900">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400 dark:bg-slate-950/40">
                <th rowSpan={2} className="w-10 p-2.5 text-center">#</th>
                <th rowSpan={2} className="p-2.5 text-left">Buyer</th>
                <th
                  rowSpan={2}
                  className={`cursor-pointer select-none p-2.5 text-center hover:text-slate-600 ${sort.key === "multiple" ? "text-slate-700 dark:text-slate-400" : ""}`}
                  onClick={() => clickSort("multiple")}
                  title="Sort by multiple"
                >
                  Multiple{arrow("multiple")}
                </th>
                <th
                  colSpan={3}
                  className={`cursor-pointer select-none border-l border-slate-200 p-1.5 text-center hover:text-slate-600 ${sort.key === "ev" ? "text-slate-700 dark:text-slate-400" : ""}`}
                  onClick={() => clickSort("ev")}
                  title="Sort by enterprise value (mid)"
                >
                  Enterprise Value{arrow("ev")}
                </th>
                <th
                  colSpan={3}
                  className={`cursor-pointer select-none border-l border-slate-200 p-1.5 text-center hover:text-slate-600 ${sort.key === "cash" ? "text-slate-700 dark:text-slate-400" : ""}`}
                  onClick={() => clickSort("cash")}
                  title="Sort by cash at closing (mid)"
                >
                  Cash at Closing{arrow("cash")}
                </th>
                <th rowSpan={2} className="border-l border-slate-200 p-2.5 text-center dark:border-slate-900">Rollover</th>
                <th rowSpan={2} className="p-2.5 text-center">Earnout</th>
                <th rowSpan={2} className="p-2.5 text-center">Seller Fin.</th>
                <th
                  rowSpan={2}
                  className={`cursor-pointer select-none p-2.5 text-center hover:text-slate-600 ${sort.key === "received" ? "text-slate-700 dark:text-slate-400" : ""}`}
                  onClick={() => clickSort("received")}
                  title="Sort by received date"
                >
                  Received{arrow("received")}
                </th>
                <th rowSpan={2} className="p-2.5 text-left">Notes</th>
              </tr>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400 dark:bg-slate-950/40">
                {(["Low", "Mid", "High"] as const).map((l, i) => (
                  <th key={`ev-${l}`} className={`p-1 text-right font-medium ${i === 0 ? "border-l border-slate-200 dark:border-slate-900" : ""}`}>{l}</th>
                ))}
                {(["Low", "Mid", "High"] as const).map((l, i) => (
                  <th key={`cash-${l}`} className={`p-1 text-right font-medium ${i === 0 ? "border-l border-slate-200 dark:border-slate-900" : ""}`}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={14} className="p-8 text-center text-slate-400">Loading…</td></tr>}
              {!isLoading && sorted.length === 0 && (
                <tr><td colSpan={14} className="p-8 text-center text-slate-400">No {tab.toUpperCase()}s with terms yet.</td></tr>
              )}
              {sorted.map((c, i) => {
                const o = c.offer;
                const t = o.terms;
                const passed = o.status === "passed";
                return (
                  <tr
                    key={o.linkId}
                    className={`cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/60 ${passed ? "opacity-45" : ""} ${i % 2 === 1 ? "bg-slate-50/50 dark:bg-slate-950/40" : ""}`}
                    onClick={() => onEdit(tab, toQueueItem(o))}
                    title="Click to edit terms"
                  >
                    <td className="p-2.5 text-center">
                      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${i === 0 && !passed ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 dark:bg-slate-900/30"}`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="p-2.5">
                      <div className="font-semibold text-slate-900 dark:text-slate-200">{o.buyerName}</div>
                      <div className="text-[10px] text-slate-400">
                        {[o.buyerFirmType, passed ? "Passed" : null].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td className="p-2.5 text-center">
                      <span className="inline-block rounded-md bg-blue-50 px-2 py-0.5 text-[12px] font-bold tabular-nums text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">
                        {formatMultipleRange(t.multipleLow, t.multipleHigh)}
                      </span>
                    </td>
                    <PointCells v={c.ev} cls="text-slate-800 dark:text-slate-300" />
                    <PointCells v={c.cash} cls="text-green-700 dark:text-green-400" />
                    <td className="border-l border-slate-100 p-2.5 text-center dark:border-slate-900">
                      {t.rolloverPct != null ? (
                        <>
                          <div className="font-semibold tabular-nums text-slate-700 dark:text-slate-400">{t.rolloverPct}%</div>
                          <div className="text-[10px] tabular-nums text-slate-400">{money(rolloverDollars(t.rolloverPct, c.ev.mid))} mid</div>
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2.5 text-center">
                      {t.earnoutAmount != null && t.earnoutAmount > 0 ? (
                        <>
                          <div className="font-semibold tabular-nums text-slate-700 dark:text-slate-400">
                            {money(t.earnoutAmount)}
                            <span className={`ml-1 rounded px-1 text-[9px] font-bold uppercase ${t.earnoutInEv ? "bg-slate-100 text-slate-500 dark:bg-slate-900/30" : "bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400"}`}>
                              {t.earnoutInEv ? "in EV" : "on top"}
                            </span>
                          </div>
                          {c.totalMid != null && <div className="text-[10px] tabular-nums text-slate-400">{money(c.totalMid)} total mid</div>}
                        </>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2.5 text-center tabular-nums">
                      {t.sellerFinancingAmount != null && t.sellerFinancingAmount > 0 ? money(t.sellerFinancingAmount) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2.5 text-center text-slate-500">{dt(o.receivedAt)}</td>
                    <td className="max-w-48 truncate p-2.5 text-slate-500" title={t.notes ?? undefined}>{t.notes ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] leading-relaxed text-slate-400">
          EV re-derives from each offer's multiple × current EBITDA{ebitda != null ? ` (${money(ebitda)})` : ""}; cash at closing = EV − rollover − seller financing − earnout when included in EV. Sorting uses the midpoint of each range. Click a column to sort, a row to edit.
        </div>
      </DialogContent>
    </Dialog>
  );
}
